import type { MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Clock, Globe, Users, Sparkles } from 'lucide-react';
import {
  formatCurrencyPrice,
  formatDuration,
  formatLanguageCodes,
  getAccessTargetLabel,
  parseAccessBonusValue,
  type AccessBonusValue,
  type PricingTier,
  type PricingTierBonusTemplate,
} from './PricingTiersTooltip';

interface PricingTierBadgeProps {
  tier: PricingTier;
  index: number;
  className?: string;
  onClick?: (tier: PricingTier) => void;
}

function getTierName(tier: PricingTier, index: number): string {
  return (
    tier.name ||
    tier.translations?.ru?.name ||
    tier.translations?.['ru']?.name ||
    `Тариф ${index + 1}`
  );
}

function getBonusName(b: PricingTierBonusTemplate): string {
  return (
    b.translations?.ru?.name ||
    b.translations?.['ru']?.name ||
    b.type ||
    `Бонус #${b.id}`
  );
}

function getBonusDescription(b: PricingTierBonusTemplate): string | undefined {
  return b.translations?.ru?.description || b.translations?.['ru']?.description;
}

interface PricingTierInfoContentProps {
  tier: PricingTier;
  index: number;
  /** Подсказка «Нажмите для редактирования» (только в кликабельном Badge). */
  showEditHint?: boolean;
  className?: string;
}

/**
 * Развёрнутая карточка тарифа (цены, привилегии, доступ, срок, языки).
 * Единый источник: используется и в tooltip PricingTierBadge, и инлайн
 * (форма создания заказа, секции заказа) — без дублирования вёрстки.
 */
export function PricingTierInfoContent({
  tier,
  index,
  showEditHint = false,
  className,
}: PricingTierInfoContentProps) {
  const tierName = getTierName(tier, index);

  const allCurrencies = Object.entries(tier.prices);
  const activeCurrencies = allCurrencies.filter(([, price]) => price > 0);
  const isFree = activeCurrencies.length === 0;

  const bonusTemplates = tier.bonusTemplates ?? [];
  const bonuses = tier.bonuses ?? [];

  const accessBonuses: Array<{ bonus: PricingTierBonusTemplate; value: AccessBonusValue }> = [];
  const privilegeBonuses: PricingTierBonusTemplate[] = [];
  for (const b of bonusTemplates) {
    const access = parseAccessBonusValue(b);
    if (access) {
      accessBonuses.push({ bonus: b, value: access });
    } else {
      privilegeBonuses.push(b);
    }
  }
  const hasPrivileges = privilegeBonuses.length > 0 || bonuses.length > 0;

  return (
    <div className={cn('p-3 space-y-3 min-w-[280px]', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="font-semibold text-sm truncate">{tierName}</span>
        </div>
        {tier.isActive ? (
          <Badge
            variant="outline"
            className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20 shrink-0"
          >
            Активен
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-[10px] bg-muted text-muted-foreground border-border shrink-0"
          >
            Неактивен
          </Badge>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Цены
        </div>
        {isFree ? (
          <div className="text-sm text-muted-foreground">Бесплатно</div>
        ) : (
          <div className="space-y-1">
            {activeCurrencies.map(([currency, price]) => {
              const isBase = currency === tier.baseCurrency;
              const promo = tier.promoPrices?.[currency];
              return (
                <div
                  key={currency}
                  className="flex items-center justify-between text-sm gap-3"
                >
                  <span
                    className={cn(
                      'flex items-center gap-1.5',
                      isBase ? 'text-popover-foreground font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {currency}
                    {isBase && (
                      <Badge
                        variant="outline"
                        className="text-[9px] h-4 px-1 bg-primary/10 text-primary border-primary/20"
                      >
                        базовая
                      </Badge>
                    )}
                  </span>
                  <span className="font-mono font-medium text-right">
                    {promo !== undefined && promo !== null && promo < price ? (
                      <>
                        <span className="line-through text-muted-foreground mr-1.5">
                          {formatCurrencyPrice(price, currency)}
                        </span>
                        <span className="text-primary">
                          {formatCurrencyPrice(promo, currency)}
                        </span>
                      </>
                    ) : (
                      formatCurrencyPrice(price, currency)
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-1.5 pt-2 border-t border-border">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Привилегии
        </div>
        {hasPrivileges ? (
          <ul className="space-y-1 text-sm">
            {privilegeBonuses.map((b) => {
              const name = getBonusName(b);
              const desc = getBonusDescription(b);
              return (
                <li key={`bt-${b.id}`} className="leading-snug">
                  <span className="text-popover-foreground">• {name}</span>
                  {desc && (
                    <span className="text-muted-foreground"> — {desc}</span>
                  )}
                </li>
              );
            })}
            {bonuses.map((b) => (
              <li key={`b-${b.id}`} className="leading-snug">
                <span className="text-popover-foreground">
                  • {b.name || b.type || `Бонус #${b.id}`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-muted-foreground">Без привилегий</div>
        )}
      </div>

      <div className="space-y-1.5 pt-2 border-t border-border">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Доступ
        </div>
        {accessBonuses.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {accessBonuses.map(({ bonus, value }) => {
              const targetLabel = getAccessTargetLabel(value.accessTargetType);
              const isQuantity = value.selectionMode === 'quantity';
              const desc = getBonusDescription(bonus);
              return (
                <li key={`acc-${bonus.id}`} className="leading-snug">
                  <span className="text-popover-foreground">
                    • {targetLabel}:{' '}
                    <span className="font-medium">
                      {isQuantity
                        ? `${value.quantity ?? 0} на выбор`
                        : value.targetName || `#${value.targetId}`}
                    </span>
                  </span>
                  {desc && (
                    <span className="text-muted-foreground"> — {desc}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-sm text-muted-foreground">Доступы не настроены</div>
        )}
        <ul className="space-y-1 text-sm text-popover-foreground pt-1">
          <li className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span>
              Срок действия:{' '}
              <span className="font-medium">
                {tier.durationDays !== null && tier.durationDays !== undefined
                  ? formatDuration(tier.durationDays)
                  : 'Бессрочно'}
              </span>
            </span>
          </li>
          {tier.userLimit !== undefined && tier.userLimit !== null && (
            <li className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span>
                Лимит пользователей:{' '}
                <span className="font-medium">{tier.userLimit}</span>
              </span>
            </li>
          )}
          {tier.isFamily && (
            <li className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span>
                Семейный тариф
                {tier.familySlots && tier.familySlots > 0
                  ? ` — до ${tier.familySlots} участн.`
                  : ''}
              </span>
            </li>
          )}
          <li className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span>
              Языки:{' '}
              <span className="font-medium">
                {tier.targetLanguages && tier.targetLanguages.length > 0
                  ? formatLanguageCodes(tier.targetLanguages)
                  : 'Все языки'}
              </span>
            </span>
          </li>
        </ul>
      </div>

      {showEditHint && (
        <div className="pt-1 text-[11px] text-muted-foreground italic">
          Нажмите для редактирования
        </div>
      )}
    </div>
  );
}

export function PricingTierBadge({ tier, index, className, onClick }: PricingTierBadgeProps) {
  const tierName = getTierName(tier, index);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) { return; }
    e.stopPropagation();
    e.preventDefault();
    onClick(tier);
  };

  const trigger = onClick ? (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center"
    >
      <Badge
        variant="outline"
        className={cn('text-xs cursor-pointer hover:bg-accent transition-colors', className)}
      >
        {tierName}
      </Badge>
    </button>
  ) : (
    <Badge variant="outline" className={cn('text-xs cursor-help', className)}>
      {tierName}
    </Badge>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="max-w-sm p-0 bg-popover text-popover-foreground border border-border shadow-md"
        arrowClassName="bg-popover fill-popover border-border"
      >
        <PricingTierInfoContent
          tier={tier}
          index={index}
          showEditHint={!!onClick}
        />
      </TooltipContent>
    </Tooltip>
  );
}
